import { TaglineBadge } from '@/components/brand/TaglineBadge';
import { X } from 'lucide-react';
import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { InquiryForm } from './InquiryForm';

// ── Types ────────────────────────────────────────────────────────

export interface InquiryContext {
    source:
        | 'journal_article'
        | 'journal_article_cta'
        | 'journal_article_bottom'
        | 'journal_landing'
        | 'agent_page'
        | 'homepage'
        | 'homepage_why_luxrally'
        | 'homepage_how_it_works'
        | 'homepage_lets_chat'
        | 'homepage_split_hero'
        | 'homepage_become_client'
        | 'guides_how_it_works'
        | 'clients_page'
        | 'benefits_page'
        | 'influencer_page'
        | 'general';
    agent_id?: number;
    agent_name?: string;
    agent_photo?: string;
    agent_slug?: string;
    article_id?: number;
    article_slug?: string;
    influencer_id?: number;
    influencer_name?: string;
    page_url: string;
}

/** Openings that show the "Or register in the app" option (homepage surfaces only). */
const APP_DOWNLOAD_SOURCES = new Set<InquiryContext['source']>(['homepage', 'homepage_lets_chat']);

interface InquiryModalState {
    openInquiry: (ctx: InquiryContext) => void;
}

// ── Context ──────────────────────────────────────────────────────

const InquiryCtx = createContext<InquiryModalState>({ openInquiry: () => {} });

export function useInquiryModal() {
    return useContext(InquiryCtx);
}

// ── Provider ─────────────────────────────────────────────────────

export function InquiryModalProvider({ children }: PropsWithChildren) {
    const [context, setContext] = useState<InquiryContext | null>(null);

    const openInquiry = useCallback((ctx: InquiryContext) => {
        setContext(ctx);
    }, []);

    return (
        <InquiryCtx.Provider value={{ openInquiry }}>
            {children}
            {context && <InquiryModal context={context} onClose={() => setContext(null)} />}
        </InquiryCtx.Provider>
    );
}

// ── Modal shell ──────────────────────────────────────────────────

function InquiryModal({ context, onClose }: { context: InquiryContext; onClose: () => void }) {
    // Escape to close
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    return createPortal(
        <div className="fixed inset-0 z-[9999] overflow-y-auto" style={{ backgroundColor: '#0b2222' }}>
            <button
                onClick={onClose}
                className="fixed top-6 right-6 z-[10000] flex h-10 w-10 cursor-pointer items-center justify-center rounded-full transition-colors"
                style={{ backgroundColor: 'rgba(245, 245, 245, 0.08)', color: 'rgba(245, 245, 245, 0.6)' }}
            >
                <X size={20} />
            </button>

            <div className="flex min-h-full items-start justify-center px-6 pt-20 pb-12 md:items-center md:pt-16 md:pb-16">
                <div className="w-full max-w-xl">
                    {/* Editorial header — mirrors /clients and the agent profile inquiry hero */}
                    <header className="mb-12 text-center md:mb-14">
                        <h2 className="lux-didot lux-didot-lg text-[#F5F5F5]">
                            {context.agent_name ? `Book with ${context.agent_name}` : 'New Client Registration'}
                        </h2>
                        <TaglineBadge
                            text="NO FEES · PRIVATE RATES · BOOKED IN MINUTES"
                            className="mx-auto mt-5 h-[9px] w-auto max-w-full text-[#F5F5F5]/50"
                        />
                    </header>
                    {/* App download only for homepage openings (the app strip, the video
                        lockup's "Sign up", the bottom banner's "Let's chat"): journal and
                        agent openings must keep routing to the advisor through tracking,
                        and the tokenised agent app links are not available from the CRM yet. */}
                    <InquiryForm
                        context={context}
                        showLogo={false}
                        hideHeader
                        showAppDownload={APP_DOWNLOAD_SOURCES.has(context.source) && !context.agent_id}
                    />
                </div>
            </div>
        </div>,
        document.body,
    );
}
